import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

// Helper function to convert a string to uppercase
const toUpperCase = (value) => {
  if (typeof value !== 'string') return value;
  return value.toUpperCase();
};

// Reusable Searchable Dropdown Component with Auto-Focus on Search (unchanged)
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Sales Order Selection Modal Component (unchanged)
const SalesOrderSelectionModal = ({ 
  open, 
  onClose, 
  customerCode, 
  onSelectSalesOrderItems,
  selectedItems = []
}) => {
  const [salesOrderItems, setSalesOrderItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempSelectedItems, setTempSelectedItems] = useState(selectedItems);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  
  useEffect(() => {
    console.log('SalesOrderSelectionModal useEffect called with:', { open, customerCode });
    if (open && customerCode) {
      fetchCustomerSalesOrderItems();
      setTempSelectedItems(selectedItems);
      setSearchTerm('');
    }
  }, [open, customerCode, selectedItems]);
  
  // Filter items when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(salesOrderItems);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = salesOrderItems.filter(item => {
      return (
        (item.SalesOrderNumber && item.SalesOrderNumber.toLowerCase().includes(term)) ||
        (item.ProductCode && item.ProductCode.toLowerCase().includes(term)) ||
        (item.ProductName && item.ProductName.toLowerCase().includes(term)) ||
        (item.Description && item.Description.toLowerCase().includes(term))
      );
    });
    
    setFilteredItems(filtered);
  }, [searchTerm, salesOrderItems]);
  
  // Update filtered items when salesOrderItems changes
  useEffect(() => {
    setFilteredItems(salesOrderItems);
  }, [salesOrderItems]);
  
  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems.length > 0) {
        const firstItem = filteredItems[0];
        if (!tempSelectedItems.includes(firstItem.SalesItemCode)) {
          setTempSelectedItems(prev => [...prev, firstItem.SalesItemCode]);
        }
      }
    }
  };
  
  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle Ctrl+S to save selection
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleApplySelection();
      }
      
      // Handle Ctrl+A to select all items
      if (e.ctrlKey && e.key === 'a') {
        e.preventDefault(); // Prevent default browser select all behavior
        if (filteredItems.length > 0) {
          setTempSelectedItems(filteredItems.map(item => item.SalesItemCode));
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, tempSelectedItems, filteredItems]);
  
  const fetchCustomerSalesOrderItems = async () => {
    console.log('Fetching customer sales order items for:', customerCode);
    try {
      setLoading(true);
      
      // First, fetch all delivery notes for this customer to determine which specific items have been delivered
      const deliveryNotesResponse = await fetch(`${API_URL}/delivery-note/customer/${customerCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      let deliveredItemCodes = new Set(); // Track individual item codes instead of order numbers
      if (deliveryNotesResponse.ok) {
        const deliveryNotes = await deliveryNotesResponse.json();
        console.log('Customer delivery notes:', deliveryNotes);
        
        // Extract all sales order item codes that have been delivered
        deliveryNotes.forEach(deliveryNote => {
          if (deliveryNote.Items && Array.isArray(deliveryNote.Items)) {
            deliveryNote.Items.forEach(item => {
              if (item.SalesItemCode) {
                deliveredItemCodes.add(item.SalesItemCode);
              }
            });
          }
        });
        
        console.log('Delivered sales order item codes:', Array.from(deliveredItemCodes));
      } else {
        console.error('Failed to fetch customer delivery notes');
      }
      
      // Now fetch the sales order items
      const response = await fetch(`${API_URL}/sales-order/customer/${customerCode}/items`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch customer sales order items');
      const data = await response.json();
      console.log('Fetched sales order items:', data);
      
      // Filter out items that have been individually delivered OR have no remaining quantity
      const filteredItems = data.filter(item => {
        // Check if this specific item has been delivered
        const hasBeenDelivered = deliveredItemCodes.has(item.SalesItemCode);
        
        // Also check if there's remaining quantity to deliver
        const hasRemainingQty = parseFloat(item.RemainingQty || item.RemainingQuantity || 0) > 0;
        
        // Only show items that haven't been delivered AND have remaining quantity
        return hasRemainingQty;
      });
      
      console.log('Filtered sales order items (non-delivered only):', filteredItems);
      setSalesOrderItems(filteredItems || []);
    } catch (error) {
      console.error('Error fetching customer sales order items:', error);
      setSalesOrderItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckboxChange = (itemCode) => {
    setTempSelectedItems(prev => {
      if (prev.includes(itemCode)) {
        return prev.filter(code => code !== itemCode);
      } else {
        return [...prev, itemCode];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (tempSelectedItems.length === filteredItems.length) {
      setTempSelectedItems([]);
    } else {
      setTempSelectedItems(filteredItems.map(item => item.SalesItemCode));
    }
  };
  
  const handleApplySelection = () => {
    console.log('Applying selection:', tempSelectedItems);
    // Get the full item objects for the selected item codes
    const selectedItemsData = filteredItems.filter(item => 
      tempSelectedItems.includes(item.SalesItemCode)
    );
    onSelectSalesOrderItems(selectedItemsData);
    onClose();
  };
  
  if (!open) return null;
  
  console.log('Rendering SalesOrderSelectionModal with open:', open);
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-[#384959]">Select Sales Order Items</h2>
          <button 
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
            onClick={() => {
              console.log('Close button clicked');
              onClose();
            }}
          >
            <CloseIcon size={24} />
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by order number, product code, name, or description..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="mt-1 text-xs text-gray-500">
  Showing {filteredItems.length} of {salesOrderItems.length} items
  {searchTerm && (
    <span className="ml-2">
      Press <span className="bg-gray-200 px-1 rounded">Enter</span> to select top result
    </span>
  )}
  <span className="ml-2">
    Press <span className="bg-gray-200 px-1 rounded">Ctrl+S</span> to save selection
  </span>
  <span className="ml-2">
    Press <span className="bg-gray-200 px-1 rounded">Ctrl+A</span> to select all
  </span>
</div>
        </div>
        
        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A89A7]"></div>
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={tempSelectedItems.length === filteredItems.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                  />
                  <label htmlFor="selectAll" className="ml-2 block text-sm font-medium text-gray-700">
                    Select All
                  </label>
                </div>
                <div className="text-sm text-gray-500">
                  {tempSelectedItems.length} of {filteredItems.length} selected
                </div>
              </div>
              
              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                        Select
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sales Order #
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        RemainingQty
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map((item) => (
                      <tr key={item.SalesItemCode} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={tempSelectedItems.includes(item.SalesItemCode)}
                            onChange={() => handleCheckboxChange(item.SalesItemCode)}
                            className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.SalesOrderNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.ProductCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.ProductName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {item.Description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.Quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.RemainingQty}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${parseFloat(item.UnitPrice || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${parseFloat(item.Total || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No sales order items found</h3>
              <p className="text-gray-500">There are no sales order items available for this customer.</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
              onClick={() => {
                console.log('Cancel button clicked');
                onClose();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
              onClick={handleApplySelection}
            >
              Apply Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// For SerialSelectionPopup component
const SerialSelectionPopup = ({ 
  open, 
  onClose, 
  serialData, 
  onSelectSerial,
  requiredQuantity,
  selectedSerials = [],
  productCode,
  warehouseCode,
  warehouseFiltering
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedSerials, setTempSelectedSerials] = useState([]);
  const [displayedSerials, setDisplayedSerials] = useState([]);
  
  // Initialize selected serials when popup opens
  useEffect(() => {
    if (open) {
      const serialNumbers = selectedSerials.map(serial => serial.serialNumber);
      setTempSelectedSerials(serialNumbers);
    }
  }, [open, selectedSerials]);
  
  // Get all serials
  const allSerials = Array.isArray(serialData) ? serialData : (serialData?.serials || []);
  
  // Filter and sort serials when search term or allSerials changes
  useEffect(() => {
    console.log('Filtering serials. Search term:', searchTerm);
    console.log('All serials:', allSerials);
    
    let filtered = [...allSerials];
    
    // Apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(serial => {
        const serialNumber = (serial.SerialNumber || '').toLowerCase();
        const locationName = (serial.LocationName || '').toLowerCase();
        const warehouseCode = (serial.WarehouseCode || '').toLowerCase();
        
        return (
          serialNumber.includes(searchLower) ||
          locationName.includes(searchLower) ||
          warehouseCode.includes(searchLower)
        );
      });
    }
    
    console.log('Filtered serials:', filtered);
    
    // Sort to show selected items first
    filtered.sort((a, b) => {
      const aSelected = tempSelectedSerials.includes(a.SerialNumber);
      const bSelected = tempSelectedSerials.includes(b.SerialNumber);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
    
    setDisplayedSerials(filtered);
  }, [allSerials, searchTerm, tempSelectedSerials]);
  
  // Handle serial selection
  const handleSerialSelect = (serialNumber) => {
    if (tempSelectedSerials.includes(serialNumber)) {
      setTempSelectedSerials(tempSelectedSerials.filter(s => s !== serialNumber));
    } else {
      setTempSelectedSerials([...tempSelectedSerials, serialNumber]);
    }
  };

  // Handle save with useCallback for stable reference
  const handleSave = useCallback(() => {
    if (tempSelectedSerials.length !== requiredQuantity) {
      alert(`Please select exactly ${requiredQuantity} serials. Currently selected: ${tempSelectedSerials.length}`);
      return;
    }
    
    const serialObjects = tempSelectedSerials.map(serialNumber => ({
      serialNumber: serialNumber
    }));
    
    onSelectSerial(serialObjects);
  }, [tempSelectedSerials, requiredQuantity, onSelectSerial]);

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (displayedSerials.length > 0) {
        const firstSerial = displayedSerials[0];
        if (!tempSelectedSerials.includes(firstSerial.SerialNumber)) {
          setTempSelectedSerials(prev => [...prev, firstSerial.SerialNumber]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleSave]);

  // Early return after all hooks are defined
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Serials</h2>
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Product:</span> 
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {productCode || 'N/A'}
              </span>
            </div>
            {warehouseFiltering && (
              <div className="mt-1 text-sm text-gray-600">
                {warehouseFiltering.enabled ? (
                  warehouseFiltering.applied ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing serials from: {warehouseFiltering.warehouseCode}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing serials from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required: {requiredQuantity} | Selected: {tempSelectedSerials.length}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by serial number, location, or warehouse..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Showing {displayedSerials.length} of {allSerials.length} serials
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {displayedSerials.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedSerials.map((serial) => (
                    <tr key={`${serial.SerialNumber}-${Math.random()}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={tempSelectedSerials.includes(serial.SerialNumber)}
                          onChange={() => handleSerialSelect(serial.SerialNumber)}
                          className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {productCode || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {serial.SerialNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="font-medium">{serial.LocationName || 'N/A'}</span>
                          {serial.WarehouseCode && (
                            <span className="ml-2 text-xs text-gray-400">({serial.WarehouseCode})</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching serials found' : 'No serials found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `There are no serials matching "${searchTerm}".` 
                  : warehouseFiltering?.enabled && warehouseFiltering?.applied
                    ? `There are no serials available for product ${productCode} in warehouse: ${warehouseFiltering.warehouseCode}`
                    : `There are no serials available for product ${productCode}.`}
              </p>
            </div>
          )}
          
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedSerials.length} of {requiredQuantity} required
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// For BatchSelectionPopup component
const BatchSelectionPopup = ({ 
  open, 
  onClose, 
  batchData, 
  onSelectBatch,
  productSettings,
  companySettings,
  requiredQuantity,
  selectedBatches = [],
  productCode,
  warehouseCode,
  warehouseFiltering
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedBatches, setTempSelectedBatches] = useState([]);
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    if (open) {
      // Ensure batchData is an array before filtering
      const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
      const initializedBatches = selectedBatches.map(selectedBatch => {
        const batchInfo = batches.find(b => b.BatchNumber === selectedBatch.batchNumber);
        return {
          batchNumber: selectedBatch.batchNumber,
          quantity: selectedBatch.quantity,
          expiryDate: selectedBatch.expiryDate,
          availableQuantity: batchInfo ? parseFloat(batchInfo.TotalQuantity) || 0 : 0
        };
      });
      setTempSelectedBatches(initializedBatches);
      setErrors({});
    }
  }, [open, selectedBatches, batchData]);
  
  // Ensure batchData is properly structured before filtering
  const batches = Array.isArray(batchData) ? batchData : (batchData?.batches || []);
  
  // Filter batch data based on search term and quantity > 0
 const filteredBatchData = batches.filter(batch => {
  // Check if this batch is already selected
  const isSelected = tempSelectedBatches.some(selected => selected.batchNumber === batch.BatchNumber);
  
  // If batch is selected, include it regardless of quantity
  if (isSelected) {
    return true;
  }
  
  // For non-selected batches, filter out batches with zero or negative quantity
  const hasQuantity = parseFloat(batch.TotalQuantity) > 0;
  
  // Apply search filter if search term is provided
  const matchesSearch = searchTerm === '' || 
    (batch.BatchNumber && batch.BatchNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (batch.LocationName && batch.LocationName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (batch.WarehouseCode && batch.WarehouseCode.toLowerCase().includes(searchTerm.toLowerCase()));
  
  return hasQuantity && matchesSearch;
}).sort((a, b) => {
  // Sort so that selected batches appear first
  const aSelected = tempSelectedBatches.some(selected => selected.batchNumber === a.BatchNumber);
  const bSelected = tempSelectedBatches.some(selected => selected.batchNumber === b.BatchNumber);
  
  if (aSelected && !bSelected) return -1;
  if (!aSelected && bSelected) return 1;
  return 0;
});

  const handleBatchSelect = (batch) => {
    const batchNumber = batch.BatchNumber;
    const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === batchNumber);
    
    if (existingIndex >= 0) {
      // Remove batch if already selected
      const newSelected = [...tempSelectedBatches];
      newSelected.splice(existingIndex, 1);
      setTempSelectedBatches(newSelected);
    } else {
      // Add new batch with quantity 0
      setTempSelectedBatches([
        ...tempSelectedBatches,
        {
          batchNumber: batch.BatchNumber,
          quantity: 0,
          expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : '',
          availableQuantity: parseFloat(batch.TotalQuantity) || 0
        }
      ]);
    }
  };

 const handleQuantityChange = (batchNumber, quantity) => {
  const newSelected = tempSelectedBatches.map(batch => {
    if (batch.batchNumber === batchNumber) {
      const qty = parseFloat(quantity) || 0;
      const availableQty = batch.availableQuantity;
      
      // Validate quantity doesn't exceed available
      if (qty > availableQty) {
        setErrors(prev => ({
          ...prev,
          [batchNumber]: `Quantity cannot exceed available quantity (${availableQty})`
        }));
        return batch;
      } else if (qty <= 0) {
        // NEW: Validate quantity is greater than zero
        setErrors(prev => ({
          ...prev,
          [batchNumber]: "Quantity must be greater than zero"
        }));
        return batch;
      } else {
        // Clear error for this batch
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[batchNumber];
          return newErrors;
        });
        return { ...batch, quantity: qty };
      }
    }
    return batch;
  });
  
  setTempSelectedBatches(newSelected);
};

  // Handle save with useCallback for stable reference
  // Update the handleSave function in BatchSelectionPopup
const handleSave = useCallback(() => {
  // NEW: Validate all batches have quantity > 0
  const newErrors = {};
  let hasZeroQuantity = false;
  
  tempSelectedBatches.forEach(batch => {
    if (batch.quantity <= 0) {
      newErrors[batch.batchNumber] = "Quantity must be greater than zero";
      hasZeroQuantity = true;
    }
  });
  
  if (hasZeroQuantity) {
    setErrors(newErrors);
    return;
  }
  
  // Calculate total selected quantity
  const totalSelected = tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0);
  
  // Validate total matches required quantity
  if (Math.abs(totalSelected - requiredQuantity) > 0.001) {
    alert(`Total quantity (${totalSelected}) must match required quantity (${requiredQuantity})`);
    return;
  }
  
  // Check for any other errors
  if (Object.keys(errors).length > 0) {
    alert('Please fix the errors before saving');
    return;
  }
  
  onSelectBatch(tempSelectedBatches);
}, [tempSelectedBatches, requiredQuantity, errors, onSelectBatch]);

  // Handle search key down events (Enter to select first)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredBatchData.length > 0) {
        const firstBatch = filteredBatchData[0];
        const existingIndex = tempSelectedBatches.findIndex(b => b.batchNumber === firstBatch.BatchNumber);
        
        if (existingIndex < 0) {
          // Add new batch with quantity 0
          setTempSelectedBatches(prev => [
            ...prev,
            {
              batchNumber: firstBatch.BatchNumber,
              quantity: 0,
              expiryDate: firstBatch.ExpiryDate ? new Date(firstBatch.ExpiryDate).toISOString().split('T')[0] : '',
              availableQuantity: parseFloat(firstBatch.TotalQuantity) || 0
            }
          ]);
        }
      }
    }
  };

  // Add global keyboard listener for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleSave]);

  // Early return after all hooks are defined
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1010 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
        <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#384959]">Select Batches</h2>
            {/* Warehouse filtering info */}
            {warehouseFiltering && (
              <div className="mt-2 text-sm text-gray-600">
                {warehouseFiltering.enabled ? (
                  warehouseFiltering.applied ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Showing batches from: {warehouseFiltering.warehouseCode}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Showing batches from all warehouses
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Warehouse filtering disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">
              Required Quantity: {requiredQuantity}
            </span>
            <button className="text-[#A0AEC0] hover:text-[#384959]" onClick={onClose}>
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex-shrink-0 p-4 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by batch number..."
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <svg 
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {filteredBatchData.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity to Use</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBatchData.map((batch) => {
                    const selectedBatch = tempSelectedBatches.find(b => b.batchNumber === batch.BatchNumber);
                    const isSelected = !!selectedBatch;
                    
                    return (
                      <tr key={batch.BatchNumber} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleBatchSelect(batch)}
                            className="h-4 w-4 text-[#6A89A7] focus:ring-[#6A89A7] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {productCode || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {batch.BatchNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="font-medium">{batch.LocationName || 'N/A'}</span>
                            {batch.WarehouseCode && (
                              <span className="ml-2 text-xs text-gray-400">({batch.WarehouseCode})</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.ExpiryDate ? new Date(batch.ExpiryDate).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {batch.TotalQuantity}
  {parseFloat(batch.TotalQuantity) <= 0 && (
    <span className="ml-2 text-xs text-red-500 font-medium">
      (Insufficient Stock)
    </span>
  )}
</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {isSelected ? (
    <div className="flex items-center">
      <input
        type="number"
        min="0" // Changed from min="1" to allow zero input but show error
        max={batch.TotalQuantity}
        value={selectedBatch.quantity}
        onChange={(e) => handleQuantityChange(batch.BatchNumber, e.target.value)}
        className={`w-20 border rounded px-2 py-1 ${
          errors[batch.BatchNumber] ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {errors[batch.BatchNumber] && (
        <span className="text-xs text-red-500 ml-2">
          {errors[batch.BatchNumber]}
        </span>
      )}
    </div>
      ) : (
    <span className="text-gray-400">Select batch</span>
  )}
</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchTerm ? 'No matching batches found' : 'No batches found'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? 'There are no batches matching your search criteria with available quantity.' 
                  : warehouseFiltering?.enabled && warehouseFiltering?.applied
                    ? `There are no batches available for this product in warehouse: ${warehouseFiltering.warehouseCode}`
                    : 'There are no batches available for this product.'}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Selected: {tempSelectedBatches.length} batches | 
              Total Quantity: {tempSelectedBatches.reduce((sum, batch) => sum + batch.quantity, 0)}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#6A89A7] hover:bg-[#5a7997] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6A89A7]"
                onClick={handleSave}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState({
    deliveryNoteNo: '',
    deliveryNoteDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    customerCode: '',
    subTotal: '0',
    totalTax: '0',
    discount: '0',
    shippingHandlingCharge: '0',
    totalAmount: '0',
    amountPaid: '0',
    balanceDue: '0',
    paymentMethod: '',
    paymentNotes: '',
    branchCode: '',
    isActive: '1', // Default to Active
    inventoryFlag: 'yes', // Default to yes - new field
    department_id: "", // Add department_id field
  });
  
  const [items, setItems] = useState([]);
  const [selectedSalesOrderItems, setSelectedSalesOrderItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    customers: [],
    taxCodes: [],
    locations: [], // Added locations
    branches: [], // Add this line
    nextDeliveryNoteNumber: '',
    nextItemCode: '',
    companySettings: null // Add company settings
  });
  const [showSalesOrderModal, setShowSalesOrderModal] = useState(false);
  const [defaultLocation, setDefaultLocation] = useState(''); // Added default location
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);

  // Batch-related state
  const [showBatchPopup, setShowBatchPopup] = useState(false);
 const [batchData, setBatchData] = useState({ batches: [], warehouseFiltering: null });
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [productSettings, setProductSettings] = useState({});
  
  // Serial-related state
  const [showSerialPopup, setShowSerialPopup] = useState(false);
const [serialData, setSerialData] = useState({ serials: [], warehouseFiltering: null });
  const [selectedSerialIndex, setSelectedSerialIndex] = useState(null);
  
  const [stockValidation, setStockValidation] = useState({});
  const [validatingStock, setValidatingStock] = useState(false);

  // Add state for batch and serial data
// const [batchData, setBatchData] = useState({ batches: [], warehouseFiltering: null });
// const [serialData, setSerialData] = useState({ serials: [], warehouseFiltering: null });
  
  // Function to fetch current user
   const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        console.log('Setting current user:', userData.user);
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  

  
  // Calculate summary values
  const calculateSummary = useMemo(() => {
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const discount = parseFloat(item.discount) || 0;
      const taxAmount = parseFloat(item.taxAmount) || 0;
      
      // Calculate item subtotal
      const itemSubTotal = quantity * unitPrice;
      subTotal += itemSubTotal;
      
      // Add discount
      totalDiscount += discount;
      
      // Add tax amount
      totalTax += taxAmount;
    });
    
    const shippingHandlingCharge = parseFloat(header.shippingHandlingCharge) || 0;
    const totalAmount = subTotal - totalDiscount + totalTax + shippingHandlingCharge;
    
    return {
      subTotal: subTotal.toFixed(2),
      totalTax: totalTax.toFixed(2),
      discount: totalDiscount.toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  }, [items, header.shippingHandlingCharge]);
  
  // Calculate balance due
  const calculateBalanceDue = useMemo(() => {
    const totalAmount = parseFloat(header.totalAmount) || 0;
    const amountPaid = parseFloat(header.amountPaid) || 0;
    return (totalAmount - amountPaid).toFixed(2);
  }, [header.totalAmount, header.amountPaid]);
  
  // Update header when summary changes
  useEffect(() => {
    if (items.length > 0) {
      setHeader(prev => ({
        ...prev,
        ...calculateSummary
      }));
    }
  }, [calculateSummary, items.length]);
  
  // Update balance due when total amount or amount paid changes
  useEffect(() => {
    setHeader(prev => ({
      ...prev,
      balanceDue: calculateBalanceDue
    }));
  }, [calculateBalanceDue]);
  
  // Debug function to log state changes
  useEffect(() => {
    console.log('InvoiceFormModal state updated:', {
      open,
      showSalesOrderModal,
      customerCode: header.customerCode,
      selectedSalesOrderItems,
      isActive: header.isActive,
      inventoryFlag: header.inventoryFlag,
      defaultLocation,
      isMaximized,
      showDepartmentField,
      showDepartmentItemField,
      showBatchPopup,
      showSerialPopup,
      companySettings
    });
  }, [open, showSalesOrderModal, header.customerCode, selectedSalesOrderItems, header.isActive, header.inventoryFlag, defaultLocation, isMaximized, showDepartmentField, showDepartmentItemField, showBatchPopup, showSerialPopup, companySettings]);
  
  // Fetch current user on mount
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
 useEffect(() => {
  const fetchData = async () => {
    if (open) {
      // Fetch current user when modal opens
      await fetchCurrentUser();
      
      // Fetch dropdown data and get the result
      const dropdownDataResult = await fetchDropdownData();
      
      if (isEdit && initialData) {
        // Fetch product settings for each item BEFORE setting items
        const productCodes = [...new Set(initialData.Items.map(item => item.ProductCode))];
        const settingsPromises = productCodes.map(code => fetchProductSettings(code));
        const settingsResults = await Promise.all(settingsPromises);
        
        // Create a map of product code to settings for easy lookup
        const settingsMap = {};
        productCodes.forEach((code, index) => {
          settingsMap[code] = settingsResults[index];
        });
        
        console.log('Fetched product settings for edit mode:', settingsMap);
        
        // Set header data for edit mode
        setHeader({
          deliveryNoteNo: initialData.DeliveryNoteNo || '',
          deliveryNoteDate: initialData.DeliveryNoteDate ? new Date(initialData.DeliveryNoteDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          dueDate: initialData.DueDate ? new Date(initialData.DueDate).toISOString().split('T')[0] : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          customerCode: initialData.CustomerCode || '',
          subTotal: initialData.SubTotal?.toString() || '0',
          totalTax: initialData.TotalTax?.toString() || '0',
          discount: initialData.Discount?.toString() || '0',
          shippingHandlingCharge: initialData.ShippingHandlingCharge?.toString() || '0',
          totalAmount: initialData.TotalAmount?.toString() || '0',
          amountPaid: initialData.AmountPaid?.toString() || '0',
          balanceDue: initialData.BalanceDue?.toString() || '0',
          paymentMethod: initialData.PaymentMethod || '',
          paymentNotes: initialData.PaymentNotes || '',
          branchCode: initialData.BranchCode || '',
          isActive: initialData.IsActive !== undefined ? (initialData.IsActive ? '1' : '0') : '1',
          inventoryFlag: initialData.InventoryFlag !== undefined ? (initialData.InventoryFlag ? 'yes' : 'no') : 'yes',
          department_id: initialData.DepartmentID?.toString() || "",
        });
        
        // Process items with proper product settings and tracking details
        if (initialData.Items && initialData.Items.length > 0) {
          // Fetch tracking details for each item
          const trackingDetailsPromises = initialData.Items.map(item => 
            fetchItemTrackingDetails(initialData.DeliveryNoteNo, item.DeliveryNoteItemCode)
          );
          const trackingDetailsResults = await Promise.all(trackingDetailsPromises);
          
          // Create a map of delivery note item code to tracking details
          const trackingDetailsMap = {};
          initialData.Items.forEach((item, index) => {
            trackingDetailsMap[item.DeliveryNoteItemCode] = trackingDetailsResults[index];
            console.log(`Tracking details for item ${item.ProductCode} (${item.DeliveryNoteItemCode}):`, trackingDetailsResults[index]);
          });
          
          // First, create a basic processed items array
          let processedItems = initialData.Items.map((item, index) => {
            const quantity = parseFloat(item.Quantity) || 0;
            const unitPrice = parseFloat(item.UnitPrice) || 0;
            const discount = parseFloat(item.Discount) || 0;
            const itemSubTotal = quantity * unitPrice - discount;
            
            // Calculate tax from taxCode
            const taxRate = getTaxRate(item.TaxCode);
            const taxAmount = itemSubTotal * (taxRate / 100);
            
            // Calculate total including tax
            const totalIncludingTax = itemSubTotal + taxAmount;
            
            // Get product settings from settingsMap
            const productSettings = settingsMap[item.ProductCode] || {
              allowBatchOnProduct: false,
              allowProductNegativeInventory: false,
              trackingType: 'none',
              allowProductTracking: 0
            };
            
            const itemTrackingDetails = trackingDetailsMap[item.DeliveryNoteItemCode] || [];
            console.log(`Processing tracking details for item ${item.ProductCode}:`, itemTrackingDetails);
            
            // Prepare batches and serials from tracking details
            let batches = [];
            let serials = [];
            
            // Check the product's tracking type
            console.log(`Product tracking type for ${item.ProductCode}:`, productSettings.trackingType);
            
            if (productSettings.trackingType === 'batch') {
              batches = itemTrackingDetails
                .filter(detail => detail.TrackingType === 'batch' || detail.TrackingType === 1)
                .map(detail => ({
                  batchNumber: detail.TrackingNumber,
                  quantity: parseFloat(detail.Quantity) || 0,
                  expiryDate: detail.ExpiryDate ? new Date(detail.ExpiryDate).toISOString().split('T')[0] : ''
                }));
              console.log(`Processed batches for ${item.ProductCode}:`, batches);
            } else if (productSettings.trackingType === 'serial') {
              serials = itemTrackingDetails
                .filter(detail => detail.TrackingType === 'serial' || detail.TrackingType === 2)
                .map(detail => ({
                  serialNumber: detail.TrackingNumber
                }));
              console.log(`Processed serials for ${item.ProductCode}:`, serials);
            }
            
            console.log(`Processing item ${item.ProductCode}:`, {
              trackingType: productSettings.trackingType,
              batches: batches,
              serials: serials
            });
            
            return {
              salesOrderNumber: item.SalesOrderNumber || '',
              salesItemCode: item.SalesItemCode || '',
              deliveryNoteItemCode: item.DeliveryNoteItemCode || '',
              productName: item.ProductName || '',
              productCode: item.ProductCode || '',
              description: item.Description || '',
              uomID: item.UOMID ? item.UOMID.toString() : '',
              quantity: quantity.toString(),
              unitPrice: unitPrice.toString(),
              discount: discount.toString(),
              taxCode: item.TaxCode || '',
              total: totalIncludingTax.toFixed(2),
              taxAmount: taxAmount.toFixed(2),
              dispatchQuantity: item.DispatchQuantity ? item.DispatchQuantity.toString() : '0',
              remainingQty: item.RemainingQty ? item.RemainingQty.toString() : '0',
              warehouseCode: item.WarehouseCode || defaultLocation,
              department_id: item.DepartmentID?.toString() || "",
              trackingType: productSettings.trackingType,
              batches: batches,
              serials: serials,
              allowBatchOnProduct: productSettings.allowBatchOnProduct,
              allowProductNegativeInventory: productSettings.allowProductNegativeInventory,
              allowProductTracking: productSettings.allowProductTracking,
              originalQuantity: item.Quantity,
            };
          });
          
          console.log('Setting items in edit mode with tracking details:', processedItems);
          setItems(processedItems);
          
          // Set selected sales order items
          setSelectedSalesOrderItems(initialData.Items.map(item => ({
            SalesItemCode: item.SalesItemCode,
            SalesOrderNumber: item.SalesOrderNumber
          })));
        }
        
        // Set customer info
        if (initialData.CustomerCode) {
          const customer = dropdownDataResult?.customers.find(c => c.customer_code === initialData.CustomerCode);
          if (customer) {
            setCustomerInfo({
              customer_code: customer.customer_code,
              customer_name: customer.customer_name
            });
          }
        }
      } else {
        // Set header data for new delivery note
        setHeader({
          deliveryNoteNo: dropdownDataResult?.nextDeliveryNoteNumber || '',
          deliveryNoteDate: new Date().toISOString().split('T')[0],
          dueDate: '', // Leave empty for user to select
          customerCode: '',
          subTotal: '0',
          totalTax: '0',
          discount: '0',
          shippingHandlingCharge: '0',
          branchCode: '', // Leave empty for user to select
          totalAmount: '0',
          amountPaid: '0',
          balanceDue: '0',
          paymentMethod: '',
          paymentNotes: '',
          isActive: '1',
          inventoryFlag: 'yes',
          department_id: "",
        });
        setItems([]);
        setSelectedSalesOrderItems([]);
        setCustomerInfo(null);
      }
      setErrors({});
      setIsSubmitting(false);
      setIsMaximized(false);
    }
  };
  
  fetchData();
}, [open, initialData, isEdit]);

 // Replace the existing fetchDropdownData function with these separate fetch functions

const fetchCustomers = async () => {
  try {
    const response = await fetch(`${API_URL}/customers`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch customers");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching customers:", error);
    return [];
  }
};

const fetchTaxCodes = async () => {
  try {
    const response = await fetch(`${API_URL}/taxes`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) throw new Error("Failed to fetch tax codes");
    const data = await response.json();
    console.log("fetching taxes.. " , data);
    return data || [];
  } catch (error) {
    console.error("Error fetching tax codes:", error);
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await fetch(`${API_URL}/locations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch locations");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching locations:", error);
    return [];
  }
};

const fetchBranches = async () => {
  try {
    const response = await fetch(`${API_URL}/branches`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch branches");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching branches:", error);
    return [];
  }
};

const fetchNextDeliveryNoteNumber = async () => {
  try {
    const response = await fetch(`${API_URL}/delivery-note/next-number`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch next delivery note number");
    const data = await response.json();
    return data.nextDeliveryNoteNumber || "";
  } catch (error) {
    console.error("Error fetching next delivery note number:", error);
    return "";
  }
};

const fetchNextItemCode = async () => {
  try {
    const response = await fetch(`${API_URL}/delivery-note/next-item-code`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch next item code");
    const data = await response.json();
    console.log("Next item code fetched:", data.nextItemCode);
    return data.nextItemCode || "";
  } catch (error) {
    console.error("Error fetching next item code:", error);
    return "";
  }
};

const fetchCompanySettings = async () => {
  try {
    const response = await fetch(`${API_URL}/company/settings`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch company settings");
    const data = await response.json();
    return data || null;
  } catch (error) {
    console.error("Error fetching company settings:", error);
    return null;
  }
};

const fetchDepartments = async () => {
  try {
    const response = await fetch(`${API_URL}/departments`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch departments");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching departments:", error);
    return [];
  }
};

const fetchScreenConfig = async () => {
  try {
    const response = await fetch(`${API_URL}/screens`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    
    // Find the Delivery Note screen configuration
    const deliveryNoteScreen = data.screens.find(screen => 
      screen.ScreenName.toLowerCase() === 'DeliveryNote'.toLowerCase()
    );
    
    return deliveryNoteScreen || null;
  } catch (error) {
    console.error("Error fetching screen configuration:", error);
    return null;
  }
};

// Update the fetchDropdownData function to use the separate fetch functions
const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      customers, 
      taxCodes, 
      locations, 
      branches, 
      nextDeliveryNoteNumber, 
      nextItemCode,
      companySettings,
      departments,
      screenConfig
    ] = await Promise.all([
      fetchCustomers(),
      fetchTaxCodes(),
      fetchLocations(),
      fetchBranches(),
      fetchNextDeliveryNoteNumber(),
      fetchNextItemCode(),
      fetchCompanySettings(),
      fetchDepartments(),
      fetchScreenConfig()
    ]);
    
    // Update the dropdown data state
    setDropdownData({
      customers,
      taxCodes,
      locations,
      branches,
      nextDeliveryNoteNumber,
      nextItemCode,
      companySettings
    });
    
       // Set departments and screen config separately
    setDepartments(departments);
    setScreenConfig(screenConfig);
    
    // Set default location if available
    if (locations && locations.length > 0) {
      setDefaultLocation(locations[0].LocationCode);
    }
    
    // Set default branch if available
    let defaultBranch = '';
    if (branches && branches.length > 0) {
      defaultBranch = branches[0].BranchCode;
    }
    
    // Set company settings if available
    if (companySettings) {
      setCompanySettings(companySettings);
    }
    
    // Show department field if ShowOnHeader is true
    const shouldShowDepartment = screenConfig?.ShowOnHeader === true;
    setShowDepartmentField(shouldShowDepartment);
    
    // Show department item field if ShowOnItem is true
    const shouldShowDepartmentItem = screenConfig?.ShowOnItem === true;
    setShowDepartmentItemField(shouldShowDepartmentItem);
    
    // Return the data for immediate use
    return {
      customers,
      taxCodes,
      locations,
      branches,
      nextDeliveryNoteNumber,
      nextItemCode,
      companySettings,
      departments,
      screenConfig,
      defaultBranch
    };
  } catch (error) {
    console.error("Error fetching dropdown data:", error);
    alert(`Error loading form data: ${error.message}`);
    return null;
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    let adjustedValue = value;
    
    // Convert text input values to uppercase, but not for date, number, or select fields
    if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
      adjustedValue = toUpperCase(value);
    }
    
    // Special handling for isActive to ensure it's always '1' or '0'
    if (name === 'isActive') {
      adjustedValue = value;
      console.log(`Status changed to: ${adjustedValue === '1' ? 'Active' : 'Inactive'}`);
    }
    
    // Special handling for inventoryFlag
    if (name === 'inventoryFlag') {
      adjustedValue = value;
      console.log(`Inventory Flag changed to: ${adjustedValue === 'yes' ? 'Yes' : 'No'}`);
    }
    
    // Validate amountPaid doesn't exceed totalAmount
    if (name === 'amountPaid') {
      const maxAmount = parseFloat(header.totalAmount) || 0;
      const enteredAmount = parseFloat(value) || 0;
      
      // If entered amount exceeds total, set it to total amount
      if (enteredAmount > maxAmount) {
        adjustedValue = maxAmount.toFixed(2);
      }
    }
    
    // If customer code changes, update customer info and reset sales orders
    if (name === 'customerCode') {
      if (adjustedValue) {
        const customer = dropdownData.customers.find(c => c.customer_code === adjustedValue);
        if (customer) {
          setCustomerInfo({
            customer_code: customer.customer_code,
            customer_name: customer.customer_name
          });
        }
      } else {
        setCustomerInfo(null);
        setSelectedSalesOrderItems([]);
        setItems([]);
      }
    }
    
    setHeader({ ...header, [name]: adjustedValue });
  };
  
  const handleOpenSalesOrderModal = () => {
    console.log('handleOpenSalesOrderModal called');
    console.log('Customer code:', header.customerCode);
    
    if (header.customerCode) {
      console.log('Setting showSalesOrderModal to true');
      setShowSalesOrderModal(true);
    } else {
      console.log('No customer selected, cannot open modal');
      alert('Please select a customer first');
    }
  };

// Update the fetchItemTrackingDetails function with more logging
const fetchItemTrackingDetails = async (deliveryNoteNo, deliveryNoteItemCode) => {
  try {
    const response = await fetch(`${API_URL}/delivery-note/${deliveryNoteNo}/item/${deliveryNoteItemCode}/tracking`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    console.log('Fetched tracking details for item:', deliveryNoteItemCode, data);
    return data;
  } catch (error) {
    console.error('Error fetching tracking details:', error);
    return [];
  }
};


  const checkStockAvailability = async (itemCode, locationCode, quantity, idx) => {
    if (!itemCode || !locationCode) {
      setStockValidation(prev => ({
        ...prev,
        [idx]: { valid: true, message: '' }
      }));
      return;
    }
    
    setValidatingStock(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('Validating stock:', { 
        itemCode, 
        locationCode, 
        quantity
      });
      
      const response = await axios.get(`${API_URL}/stock/check`, {
        params: {
          itemCode,
          locationCode: locationCode,
          asOfDate: new Date().toISOString(),
          quantity: quantity
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const { 
        currentStock, 
        allowed, 
        message,
        companySettings,
        productSettings
      } = response.data;
      
      console.log('Stock validation response:', response.data);
      
      setStockValidation(prev => ({
        ...prev,
        [idx]: { 
          valid: allowed, 
          message: message, 
          currentStock,
          companySettings,
          productSettings
        }
      }));
      
      return {
        allowed,
        currentStock,
        message
      };
    } catch (error) {
      console.error('Error validating stock:', error);
      
      let errorMessage = 'Error checking stock availability';
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        if (error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
          if (error.response.data.error) {
            errorMessage += `: ${error.response.data.error}`;
          }
        }
      } else if (error.request) {
        errorMessage = 'No response from server';
      } else {
        errorMessage = error.message;
      }
      
      setStockValidation(prev => ({
        ...prev,
        [idx]: { valid: false, message: errorMessage }
      }));
      
      return {
        allowed: false,
        currentStock: 0,
        message: errorMessage
      };
    } finally {
      setValidatingStock(false);
    }
  };
  
// Update the fetchBatchData function to properly handle AllowProductTracking
const fetchBatchData = async (productCode, warehouseCode) => {
  try {
    // Use string concatenation instead of URL constructor for better compatibility
    let url = `${API_URL}/products/${productCode}/batches`;
    
    // Add warehouse code as query parameter if provided
    if (warehouseCode) {
      url += `?warehouseCode=${encodeURIComponent(warehouseCode)}`;
    }
    
    console.log('Fetching batch data for:', { productCode, warehouseCode, url });
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Fetched batch data:', data);
    setBatchData(data);
  } catch (error) {
    console.error('Error fetching batch data:', error);
    setBatchData({ batches: [], warehouseFiltering: null });
  }
};

const fetchSerialData = async (productCode, warehouseCode) => {
  try {
    let url = `${API_URL}/products/${productCode}/serials`;
    
    if (warehouseCode) {
      url += `?warehouseCode=${encodeURIComponent(warehouseCode)}`;
    }
    
    console.log('Fetching serial data for:', { productCode, warehouseCode, url });
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Fetched serial data:', data);
    setSerialData(data);
  } catch (error) {
    console.error('Error fetching serial data:', error);
    setSerialData({ serials: [], warehouseFiltering: null });
  }
};


  
 // Update the handleBatchClick function to check tracking type
const handleBatchClick = (index, productCode, warehouseCode) => {
  const item = items[index];
  
  // Check if product supports batch tracking
  if (item.trackingType !== 'batch') {
    console.error('Product does not support batch tracking');
    return;
  }
  
  setSelectedBatchIndex(index);
  console.log('handleBatchClick called for index:', index, 'productCode:', productCode, 'warehouseCode:', warehouseCode);
  fetchBatchData(productCode, warehouseCode);
  console.log('Opening batch selection popup');
  setShowBatchPopup(true);
};

// Update the handleSerialClick function to check tracking type
// Update the handleSerialClick function
const handleSerialClick = (index, productCode, warehouseCode) => {
  const item = items[index];
  
  // Check if product supports serial tracking
  if (item.trackingType !== 'serial') {
    console.error('Product does not support serial tracking');
    return;
  }
  
  setSelectedSerialIndex(index);
  console.log('handleSerialClick called for index:', index, 'productCode:', productCode, 'warehouseCode:', warehouseCode);
  console.log('Current serials for this item:', item.serials);
  fetchSerialData(productCode, warehouseCode);
  console.log('Opening serial selection popup');
  setShowSerialPopup(true);
};
  
  // Function to get product settings
// Update the fetchProductSettings function to correctly handle AllowProductTracking
const fetchProductSettings = async (productCode) => {
  try {
    const response = await fetch(`${API_URL}/products/${productCode}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    console.log('Fetched product settings for', productCode, data);
    
    // Determine tracking type based on AllowProductTracking value
    let trackingType = 'none';
    if (data.AllowProductTracking === 1) {
      trackingType = 'batch';
    } else if (data.AllowProductTracking === 2) {
      trackingType = 'serial';
    }
    
    // Update the state
    setProductSettings(prev => ({
      ...prev,
      [productCode]: {
        allowBatchOnProduct: data.AllowBatchOnProduct,
        allowProductNegativeInventory: data.AllowProductNegativeInventory,
        trackingType: trackingType,
        allowProductTracking: data.AllowProductTracking // Store the raw value
      }
    }));
    
    // Return the settings for immediate use
    return {
      allowBatchOnProduct: data.AllowBatchOnProduct,
      allowProductNegativeInventory: data.AllowProductNegativeInventory,
      trackingType: trackingType,
      allowProductTracking: data.AllowProductTracking
    };
  } catch (error) {
    console.error('Error fetching product settings:', error);
    // Return default values in case of error
    return {
      allowBatchOnProduct: false,
      allowProductNegativeInventory: false,
      trackingType: 'none',
      allowProductTracking: 0
    };
  }
};
  
const handleSalesOrderItemsSelection = async (selectedItems) => {
  console.log('handleSalesOrderItemsSelection called with:', selectedItems);
  setShowSalesOrderModal(false);

  if (selectedItems.length > 0) {
    try {
      // Fetch product settings for each item and wait for all to complete
      const productCodes = [...new Set(selectedItems.map(item => item.ProductCode))];
      const settingsPromises = productCodes.map(code => fetchProductSettings(code));
      const settingsResults = await Promise.all(settingsPromises);
      
      // Create a map of product code to settings for easy lookup
      const settingsMap = {};
      productCodes.forEach((code, index) => {
        settingsMap[code] = settingsResults[index];
      });
      
      console.log('All product settings fetched:', settingsMap);
      
      // Now create the new delivery note items with the fetched settings
      const newDeliveryNoteItems = selectedItems.map(item => {
        const quantity = parseFloat(item.Quantity) || 0;
        const unitPrice = parseFloat(item.UnitPrice) || 0;
        const discount = parseFloat(item.Discount) || 0;
        
        const itemSubTotal = quantity * unitPrice - discount;
        const taxRate = getTaxRate(item.TaxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;

        // Get the settings for this product
        const productSettings = settingsMap[item.ProductCode] || {
          allowBatchOnProduct: false,
          allowProductNegativeInventory: false,
          trackingType: 'none',
          allowProductTracking: 0
        };

        return {
          salesOrderNumber: item.SalesOrderNumber,
          salesItemCode: item.SalesItemCode,
          productName: item.ProductName,
          productCode: item.ProductCode,
          description: item.Description,
          uomID: item.UOMID,
          quantity: '',
          unitPrice: item.UnitPrice,
          discount: item.Discount || 0,
          taxCode: item.TaxCode,
          total: totalIncludingTax.toFixed(2),
          taxAmount: taxAmount.toFixed(2),
          dispatchQuantity: item.DispatchQuantity || 0,
          remainingQty: item.RemainingQty || item.Quantity,
          warehouseCode: item.LocationCode || defaultLocation,
          department_id: "",
          trackingType: productSettings.trackingType || 'none',
          batches: [],
          serials: [],
          allowBatchOnProduct: productSettings.allowBatchOnProduct,
          allowProductNegativeInventory: productSettings.allowProductNegativeInventory,
          allowProductTracking: productSettings.allowProductTracking || 0,
          originalQuantity: 0, // For new items, original quantity is 0
        };
      });

      // Merge new items with existing ones, avoiding duplicates
      setItems(prevItems => {
        const existingSalesItemCodes = new Set(prevItems.map(i => i.salesItemCode));
        const filteredNewItems = newDeliveryNoteItems.filter(
          newItem => !existingSalesItemCodes.has(newItem.salesItemCode)
        );
        return [...prevItems, ...filteredNewItems];
      });
      
      console.log('Items updated with product settings');
    } catch (error) {
      console.error('Error in handleSalesOrderItemsSelection:', error);
      // Fallback: add items without waiting for product settings
      const newDeliveryNoteItems = selectedItems.map(item => {
        const quantity = parseFloat(item.Quantity) || 0;
        const unitPrice = parseFloat(item.UnitPrice) || 0;
        const discount = parseFloat(item.Discount) || 0;
        
        const itemSubTotal = quantity * unitPrice - discount;
        const taxRate = getTaxRate(item.TaxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;

        return {
          salesOrderNumber: item.SalesOrderNumber,
          salesItemCode: item.SalesItemCode,
          productName: item.ProductName,
          productCode: item.ProductCode,
          description: item.Description,
          uomID: item.UOMID,
          quantity: '',
          unitPrice: item.UnitPrice,
          discount: item.Discount || 0,
          taxCode: item.TaxCode,
          total: totalIncludingTax.toFixed(2),
          taxAmount: taxAmount.toFixed(2),
          dispatchQuantity: item.DispatchQuantity || 0,
          remainingQty: item.RemainingQty || item.Quantity,
          warehouseCode: item.LocationCode || defaultLocation,
          department_id: "",
          trackingType: 'none',
          batches: [],
          serials: [],
          allowBatchOnProduct: false,
          allowProductNegativeInventory: false,
          allowProductTracking: 0,
          originalQuantity: 0,
        };
      });

      setItems(prevItems => {
        const existingSalesItemCodes = new Set(prevItems.map(i => i.salesItemCode));
        const filteredNewItems = newDeliveryNoteItems.filter(
          newItem => !existingSalesItemCodes.has(newItem.salesItemCode)
        );
        return [...prevItems, ...filteredNewItems];
      });
    }
  }
};
  
  const getTaxRate = (taxCode) => {
    if (!taxCode || !dropdownData.taxCodes || dropdownData.taxCodes.length === 0) return 0;
    const tax = dropdownData.taxCodes.find(t => t.TaxCode === taxCode);
    return tax ? tax.Rate : 0;
  };
  
 const handleItemChange = async (idx, e) => {
  const { name, value } = e.target;
  const newItems = [...items];
  const item = newItems[idx];
  
  // Convert text input values to uppercase, but not for number or select fields
  if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
    newItems[idx][name] = toUpperCase(value);
  } else {
    // Special handling for quantity to implement the required validation logic
    if (name === 'quantity') {
      const originalQty = parseFloat(item.originalQuantity) || 0;
      const newQuantity = parseFloat(value) || 0;
      const remainingQty = parseFloat(item.remainingQty) || 0;
      
      // Calculate the difference between new quantity and original quantity
      const quantityDifference = newQuantity - originalQty;
      
      // Check if negative inventory is allowed at both company and product levels
      const allowNegative = companySettings?.AllowNegativeInventory && item.allowProductNegativeInventory;
      
      // For batch products
      if (item.trackingType === 'batch') {
        console.log('Batch quantity validation:', {
          originalQty,
          newQuantity,
          quantityDifference,
          allowNegative,
          remainingQty,
          isEdit
        });
        
        // If negative inventory is allowed, skip batch quantity check
        if (!allowNegative) {
          // If in edit mode and user is increasing quantity, validate against selected batches
          if (isEdit && quantityDifference > 0) {
            // Calculate total available from selected batches
            const totalBatchQuantity = item.batches.reduce((sum, batch) => sum + batch.quantity, 0);
            
            if (quantityDifference > totalBatchQuantity) {
              // Adjust quantity to original + available batch quantity
              const adjustedQuantity = originalQty + totalBatchQuantity;
              newItems[idx].quantity = adjustedQuantity.toString();
              alert(`Cannot increase quantity beyond available batch quantity (${totalBatchQuantity})`);
              
              // Recalculate totals
              const unitPrice = parseFloat(item.unitPrice) || 0;
              const discount = parseFloat(item.discount) || 0;
              const itemSubTotal = adjustedQuantity * unitPrice - discount;
              const taxRate = getTaxRate(item.taxCode);
              const taxAmount = itemSubTotal * (taxRate / 100);
              const totalIncludingTax = itemSubTotal + taxAmount;
              
              newItems[idx].total = totalIncludingTax.toFixed(2);
              newItems[idx].taxAmount = taxAmount.toFixed(2);
              setItems(newItems);
              return;
            }
          }
        }
        
        // Always enforce remaining quantity limit regardless of negative inventory settings
        if (newQuantity > remainingQty) {
          // Adjust quantity to remaining quantity
          newItems[idx].quantity = remainingQty.toString();
          alert(`Quantity cannot exceed remaining quantity (${remainingQty})`);
          
          // Recalculate totals
          const unitPrice = parseFloat(item.unitPrice) || 0;
          const discount = parseFloat(item.discount) || 0;
          const itemSubTotal = remainingQty * unitPrice - discount;
          const taxRate = getTaxRate(item.taxCode);
          const taxAmount = itemSubTotal * (taxRate / 100);
          const totalIncludingTax = itemSubTotal + taxAmount;
          
          newItems[idx].total = totalIncludingTax.toFixed(2);
          newItems[idx].taxAmount = taxAmount.toFixed(2);
          setItems(newItems);
          return;
        }
        
        // If quantity is valid (decreasing or increasing within available limits)
        newItems[idx].quantity = newQuantity.toString();
        
        // Recalculate totals
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discount = parseFloat(item.discount) || 0;
        const itemSubTotal = newQuantity * unitPrice - discount;
        const taxRate = getTaxRate(item.taxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;
        
        newItems[idx].total = totalIncludingTax.toFixed(2);
        newItems[idx].taxAmount = taxAmount.toFixed(2);
      } 
      // For serial products
      else if (item.trackingType === 'serial') {
        console.log('Serial quantity validation:', {
          originalQty,
          newQuantity,
          quantityDifference,
          remainingQty,
          allowNegative,
          isEdit
        });
        
        // If negative inventory is allowed, skip stock check
        if (!allowNegative) {
          // If in edit mode and user is increasing quantity, validate against selected serials
          if (isEdit && quantityDifference > 0) {
            // Calculate total available from selected serials
            const totalSerialQuantity = item.serials.length;
            
            if (quantityDifference > totalSerialQuantity) {
              // Adjust quantity to original + available serial quantity
              const adjustedQuantity = originalQty + totalSerialQuantity;
              newItems[idx].quantity = adjustedQuantity.toString();
              alert(`Cannot increase quantity beyond available serials (${totalSerialQuantity})`);
              
              // Recalculate totals
              const unitPrice = parseFloat(item.unitPrice) || 0;
              const discount = parseFloat(item.discount) || 0;
              const itemSubTotal = adjustedQuantity * unitPrice - discount;
              const taxRate = getTaxRate(item.taxCode);
              const taxAmount = itemSubTotal * (taxRate / 100);
              const totalIncludingTax = itemSubTotal + taxAmount;
              
              newItems[idx].total = totalIncludingTax.toFixed(2);
              newItems[idx].taxAmount = taxAmount.toFixed(2);
              setItems(newItems);
              return;
            }
          }
        }
        
        // Always enforce remaining quantity limit regardless of negative inventory settings
        if (newQuantity > remainingQty) {
          // Adjust quantity to remaining quantity
          newItems[idx].quantity = remainingQty.toString();
          alert(`Quantity cannot exceed remaining quantity (${remainingQty})`);
          
          // Recalculate totals
          const unitPrice = parseFloat(item.unitPrice) || 0;
          const discount = parseFloat(item.discount) || 0;
          const itemSubTotal = remainingQty * unitPrice - discount;
          const taxRate = getTaxRate(item.taxCode);
          const taxAmount = itemSubTotal * (taxRate / 100);
          const totalIncludingTax = itemSubTotal + taxAmount;
          
          newItems[idx].total = totalIncludingTax.toFixed(2);
          newItems[idx].taxAmount = taxAmount.toFixed(2);
          setItems(newItems);
          return;
        }
        
        // If quantity is valid (decreasing or increasing within available limits)
        newItems[idx].quantity = newQuantity.toString();
        
        // Recalculate totals
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discount = parseFloat(item.discount) || 0;
        const itemSubTotal = newQuantity * unitPrice - discount;
        const taxRate = getTaxRate(item.taxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;
        
        newItems[idx].total = totalIncludingTax.toFixed(2);
        newItems[idx].taxAmount = taxAmount.toFixed(2);
      }
      // For non-tracked products
      else {
        console.log('Non-tracked quantity validation:', {
          originalQty,
          newQuantity,
          quantityDifference,
          remainingQty,
          allowNegative,
          isEdit
        });
        
        // If negative inventory is allowed, skip stock check
        if (!allowNegative && item.warehouseCode) {
          // Check stock availability only if negative inventory is not allowed
          const stockCheck = await checkStockAvailability(
            item.productCode,
            item.warehouseCode,
            newQuantity,
            idx
          );
          
          // If in edit mode and stock validation fails and user is increasing quantity
          if (isEdit && !stockCheck.allowed && quantityDifference > 0) {
            // Adjust quantity to original + available stock
            const currentStock = parseFloat(stockCheck.currentStock) || 0;
            const adjustedQuantity = Math.min(originalQty + currentStock, remainingQty);
            newItems[idx].quantity = adjustedQuantity.toString();
            alert(stockCheck.message);
            
            // Recalculate totals
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const discount = parseFloat(item.discount) || 0;
            const itemSubTotal = adjustedQuantity * unitPrice - discount;
            const taxRate = getTaxRate(item.taxCode);
            const taxAmount = itemSubTotal * (taxRate / 100);
            const totalIncludingTax = itemSubTotal + taxAmount;
            
            newItems[idx].total = totalIncludingTax.toFixed(2);
            newItems[idx].taxAmount = taxAmount.toFixed(2);
            setItems(newItems);
            return;
          }
        }
        
        // Always enforce remaining quantity limit regardless of negative inventory settings
        if (newQuantity > remainingQty) {
          // Adjust quantity to remaining quantity
          newItems[idx].quantity = remainingQty.toString();
          alert(`Quantity cannot exceed remaining quantity (${remainingQty})`);
          
          // Recalculate totals
          const unitPrice = parseFloat(item.unitPrice) || 0;
          const discount = parseFloat(item.discount) || 0;
          const itemSubTotal = remainingQty * unitPrice - discount;
          const taxRate = getTaxRate(item.taxCode);
          const taxAmount = itemSubTotal * (taxRate / 100);
          const totalIncludingTax = itemSubTotal + taxAmount;
          
          newItems[idx].total = totalIncludingTax.toFixed(2);
          newItems[idx].taxAmount = taxAmount.toFixed(2);
          setItems(newItems);
          return;
        }
        
        // If quantity is valid (decreasing or increasing within available limits)
        newItems[idx].quantity = newQuantity.toString();
        
        // Recalculate totals
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discount = parseFloat(item.discount) || 0;
        const itemSubTotal = newQuantity * unitPrice - discount;
        const taxRate = getTaxRate(item.taxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;
        
        newItems[idx].total = totalIncludingTax.toFixed(2);
        newItems[idx].taxAmount = taxAmount.toFixed(2);
      }
    } else if (name === 'warehouseCode') {
      newItems[idx][name] = value;
      
      // If quantity is set, check stock availability for the new warehouse
      const quantity = parseFloat(item.quantity) || 0;
      const remainingQty = parseFloat(item.remainingQty) || 0;
      
      if (quantity > 0 && value) {
        console.log('Checking stock for warehouse change:', {
          productCode: item.productCode,
          warehouseCode: value,
          quantity: quantity
        });
        
        // Check if negative inventory is allowed at both company and product levels
        const allowNegative = companySettings?.AllowNegativeInventory && item.allowProductNegativeInventory;
        
        // Only check stock if negative inventory is not allowed
        if (!allowNegative) {
          const stockCheck = await checkStockAvailability(
            item.productCode,
            value,
            quantity,
            idx
          );
          
          // If stock validation fails, adjust quantity to available stock
          if (!stockCheck.allowed) {
            // The maximum allowed quantity is the minimum of remainingQty and currentStock
            const maxAllowedQty = Math.min(remainingQty, stockCheck.currentStock);
            newItems[idx].quantity = maxAllowedQty.toString();
            alert(stockCheck.message);
            
            // Recalculate totals since quantity changed
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const discount = parseFloat(item.discount) || 0;
            const itemSubTotal = maxAllowedQty * unitPrice - discount;
            const taxRate = getTaxRate(item.taxCode);
            const taxAmount = itemSubTotal * (taxRate / 100);
            const totalIncludingTax = itemSubTotal + taxAmount;
            
            newItems[idx].total = totalIncludingTax.toFixed(2);
            newItems[idx].taxAmount = taxAmount.toFixed(2);
          }
        }
      }
    } else {
      newItems[idx][name] = value;
    }
  }
  
  // If quantity, unit price, discount, or tax code changed, recalculate item total and tax
  if (name === 'quantity' || name === 'unitPrice' || name === 'discount' || name === 'taxCode') {
    const quantity = parseFloat(newItems[idx].quantity) || 0;
    const unitPrice = parseFloat(newItems[idx].unitPrice) || 0;
    const discount = parseFloat(newItems[idx].discount) || 0;
    
    // Calculate item subtotal (quantity * unit price - discount)
    const itemSubTotal = quantity * unitPrice - discount;
    
    // Calculate tax amount using the tax rate for this tax code
    const taxRate = getTaxRate(newItems[idx].taxCode);
    const taxAmount = itemSubTotal * (taxRate / 100);
    
    // Calculate total including tax
    const totalIncludingTax = itemSubTotal + taxAmount;
    
    newItems[idx].total = totalIncludingTax.toFixed(2);
    newItems[idx].taxAmount = taxAmount.toFixed(2);
  }
  
  setItems(newItems);
};
  
  // Function to handle batch selection
  const handleBatchSelect = (selectedBatches) => {
    if (selectedBatchIndex !== null) {
      const newItems = [...items];
      const item = newItems[selectedBatchIndex];
      const currentQty = parseFloat(item.quantity) || 0;
      
      // Update the item with selected batches
      newItems[selectedBatchIndex] = {
        ...item,
        batches: selectedBatches
      };
      
      // If no quantity is set yet, set it to the total of selected batches
      if (currentQty === 0) {
        const totalBatchQty = selectedBatches.reduce((sum, batch) => sum + batch.quantity, 0);
        newItems[selectedBatchIndex].quantity = totalBatchQty.toString();
        
        // Recalculate totals
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discount = parseFloat(item.discount) || 0;
        const itemSubTotal = totalBatchQty * unitPrice - discount;
        const taxRate = getTaxRate(item.taxCode);
        const taxAmount = itemSubTotal * (taxRate / 100);
        const totalIncludingTax = itemSubTotal + taxAmount;
        
        newItems[selectedBatchIndex].total = totalIncludingTax.toFixed(2);
        newItems[selectedBatchIndex].taxAmount = taxAmount.toFixed(2);
      }
      
      setItems(newItems);
      setShowBatchPopup(false);
    }
  };
  
 const handleSerialSelect = (selectedSerialObjects) => {
  if (selectedSerialIndex !== null) {
    const newItems = [...items];
    const item = newItems[selectedSerialIndex];
    const currentQty = parseFloat(item.quantity) || 0;
    
    // Update the item with selected serials
    newItems[selectedSerialIndex] = {
      ...item,
      serials: selectedSerialObjects
    };
    
    // If no quantity is set yet, set it to the number of selected serials
    if (currentQty === 0) {
      newItems[selectedSerialIndex].quantity = selectedSerialObjects.length.toString();
      
      // Recalculate totals
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const discount = parseFloat(item.discount) || 0;
      const itemSubTotal = selectedSerialObjects.length * unitPrice - discount;
      const taxRate = getTaxRate(item.taxCode);
      const taxAmount = itemSubTotal * (taxRate / 100);
      const totalIncludingTax = itemSubTotal + taxAmount;
      
      newItems[selectedSerialIndex].total = totalIncludingTax.toFixed(2);
      newItems[selectedSerialIndex].taxAmount = taxAmount.toFixed(2);
    }
    
    setItems(newItems);
    setShowSerialPopup(false);
  }
};
  
  const validate = () => {
    const errs = {};
    if (!header.deliveryNoteNo || !header.deliveryNoteNo.trim()) errs.deliveryNoteNo = 'Required';
    if (!header.deliveryNoteDate) errs.deliveryNoteDate = 'Required';
    if (!header.dueDate) errs.dueDate = 'Required';
    if (!header.customerCode) errs.customerCode = 'Required';
    if (items.length === 0) errs.items = 'At least one item is required';
    
    // Validate department field only if it's shown
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    // Validate that each item has a warehouse code and valid quantity
    items.forEach((item, idx) => {
      if (!item.warehouseCode) {
        errs[`warehouseCode_${idx}`] = 'Warehouse is required for each item';
      }
      
      // Validate quantity
      const quantity = parseFloat(item.quantity) || 0;
      const remainingQty = parseFloat(item.remainingQty) || 0;
      
      if (!item.quantity || quantity <= 0) {
        errs[`quantity_${idx}`] = 'Quantity must be greater than 0';
      }
      
      if (quantity > remainingQty) {
        errs[`quantity_${idx}`] = `Quantity cannot exceed remaining quantity (${remainingQty})`;
      }
      
      // Validate tracking based on tracking type
      if (item.trackingType === 'batch') {
        if (!item.batches || item.batches.length === 0) {
          errs[`batches_${idx}`] = 'At least one batch must be selected';
        } else {
          // Validate total batch quantity matches item quantity
          const totalBatchQty = item.batches.reduce((sum, batch) => sum + batch.quantity, 0);
          if (Math.abs(totalBatchQty - quantity) > 0.001) {
            errs[`batches_${idx}`] = `Total batch quantity (${totalBatchQty}) must match item quantity (${quantity})`;
          }
        }
      } else if (item.trackingType === 'serial') {
        if (!item.serials || item.serials.length === 0) {
          errs[`serials_${idx}`] = 'At least one serial must be selected';
        } else if (item.serials.length !== quantity) {
          errs[`serials_${idx}`] = `Number of serials (${item.serials.length}) must match item quantity (${quantity})`;
        }
      }
      
      // Validate department item field only if it's shown
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
    });
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  // Function to generate a unique delivery note item code
  const generateDeliveryNoteItemCode = (index) => {
    // Don't generate a code here - let the backend handle it
    return '';
  };

  const handleDeleteItem = (index) => {
  const newItems = [...items];
  const deletedItem = newItems[index];
  
  // Remove the item at the specified index
  newItems.splice(index, 1);
  
  // Update the items state
  setItems(newItems);
  
  // Also remove from selected sales order items if it exists
  if (deletedItem.salesItemCode) {
    setSelectedSalesOrderItems(prev => 
      prev.filter(item => item.SalesItemCode !== deletedItem.salesItemCode)
    );
  }
};
  
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate() || isSubmitting) return;
  
  // Enhanced user validation
  if (!isUserValid || !currentUser || !currentUser.UserID) {
    console.error('User validation failed:', {
      isUserValid,
      currentUser: currentUser ? 'exists' : 'null',
      userID: currentUser ? currentUser.UserID : 'not available'
    });
    alert(authError || 'User information not available. Please refresh the page and log in again.');
    return;
  }
  
  setIsSubmitting(true);
  setLoading(true);
  
  try {
    const itemsToSubmit = [...items];
    
    // Prepare user information with proper fallbacks
    const userId = currentUser.UserID || currentUser.id || currentUser.userId;
    const userName = currentUser.UserName || currentUser.username || currentUser.name || 'Unknown';
    
    console.log('User information for submission:', {
      userId: userId,
      userName: userName,
      fullUser: currentUser
    });
    
    // Get current timestamp in ISO format
    const now = new Date().toISOString();
    
    // Process items to ensure proper serial and batch formatting
    const processedItems = itemsToSubmit.map(item => {
      // Create a clean item object
      const processedItem = {
        ...item,
        // Only include Serials if tracking type is serial and there are actual serials
        ...(item.trackingType === 'serial' && item.serials && item.serials.length > 0 ? {
          Serials: item.serials.map(serial => {
            // Ensure serialNumber is a valid string
            const serialNumber = serial.serialNumber || serial.SerialNumber || serial;
            return {
              SerialNumber: serialNumber && typeof serialNumber === 'string' ? serialNumber.trim() : ''
            };
          }).filter(serial => serial.SerialNumber !== '') // Filter out empty serial numbers
        } : {}),
        // Only include Batches if tracking type is batch and there are actual batches
        ...(item.trackingType === 'batch' && item.batches && item.batches.length > 0 ? {
          Batches: item.batches.map(batch => {
            // Ensure batchNumber is a valid string
            const batchNumber = batch.batchNumber || batch.BatchNumber || batch;
            return {
              BatchNumber: batchNumber && typeof batchNumber === 'string' ? batchNumber.trim() : '',
              Quantity: parseFloat(batch.quantity || batch.Quantity || 0),
              ExpiryDate: batch.expiryDate || batch.ExpiryDate || null
            };
          }).filter(batch => batch.BatchNumber !== '') // Filter out empty batch numbers
        } : {})
      };
      
      return processedItem;
    });
    
    const payload = {
      DeliveryNoteNo: header.deliveryNoteNo,
      DeliveryNoteDate: header.deliveryNoteDate,
      DueDate: header.dueDate,
      CustomerCode: header.customerCode,
      SubTotal: parseFloat(header.subTotal) || 0,
      TotalTax: parseFloat(header.totalTax) || 0,
      Discount: parseFloat(header.discount) || 0,
      ShippingHandlingCharge: parseFloat(header.shippingHandlingCharge) || 0,
      TotalAmount: parseFloat(header.totalAmount) || 0,
      AmountPaid: parseFloat(header.amountPaid) || 0,
      BalanceDue: parseFloat(header.balanceDue) || 0,
      PaymentMethod: header.paymentMethod,
      PaymentNotes: header.paymentNotes,
      BranchCode: header.branchCode,
      IsActive: header.isActive,
      InventoryFlag: header.inventoryFlag === 'yes',
      // Ensure these fields are always included
      CreatedAt: isEdit ? undefined : now,
      UpdatedAt: now,
      CreatedBy: isEdit ? undefined : userId.toString(),
      UpdatedBy: userId.toString(),
      ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
      Items: processedItems.map((item, index) => ({
        SalesOrderNumber: item.salesOrderNumber,
        SalesItemCode: item.salesItemCode,
        Description: item.description,
        UOMID: parseInt(item.uomID) || 0,
        Quantity: parseFloat(item.quantity) || 0,
        UnitPrice: parseFloat(item.unitPrice) || 0,
        Discount: parseFloat(item.discount) || 0,
        TaxCode: item.taxCode,
        Total: parseFloat(item.total) || 0,
        Action: isEdit ? 'Update' : 'Add',
        ProductName: item.productName,
        ProductCode: item.productCode,
        DispatchQuantity: parseFloat(item.dispatchQuantity) || 0,
        RemainingQty: parseFloat(item.remainingQty) || 0,
        DeliveryNoteItemCode: isEdit && item.deliveryNoteItemCode ? item.deliveryNoteItemCode : undefined,
        WarehouseCode: item.warehouseCode,
        // Add tracking information based on tracking type
        ...(item.trackingType === 'batch' && item.Batches && item.Batches.length > 0 ? {
          Batches: item.Batches
        } : {}),
        ...(item.trackingType === 'serial' && item.Serials && item.Serials.length > 0 ? {
          Serials: item.Serials
        } : {}),
        ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
      }))
    };
    
    console.log('Submitting payload with user info:', {
      CreatedAt: payload.CreatedAt,
      UpdatedAt: payload.UpdatedAt,
      CreatedBy: payload.CreatedBy,
      UpdatedBy: payload.UpdatedBy,
      userId: userId,
      userName: userName
    });
    
    const url = isEdit 
      ? `${API_URL}/delivery-note/${header.deliveryNoteNo}`
      : `${API_URL}/delivery-note`;
    
    const method = isEdit ? 'PUT' : 'POST';
    
    // Add more detailed logging for the request
    console.log(`Making ${method} request to:`, url);
    console.log('Request payload:', JSON.stringify(payload, null, 2));
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error response:', errorData);
      throw new Error(errorData.message || `Failed to save delivery note: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('Successful response:', result);
    
    if (onSubmit) {
      onSubmit();
    }

    
    onClose();
  } catch (err) {
    console.error('Error saving delivery note:', err);
    alert(`Error saving delivery note: ${err.message}`);
  } finally {
    setIsSubmitting(false);
    setLoading(false);
  }
};
  
  // Toggle maximize/minimize state
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  if (!open) return null;
  
  console.log('Rendering InvoiceFormModal with showSalesOrderModal:', showSalesOrderModal);
  
  return (
    <>
      <SalesOrderSelectionModal
        open={showSalesOrderModal}
        onClose={() => {
          console.log('SalesOrderSelectionModal onClose called');
          setShowSalesOrderModal(false);
        }}
        customerCode={header.customerCode}
        onSelectSalesOrderItems={handleSalesOrderItemsSelection}
        selectedItems={selectedSalesOrderItems.map(item => item.SalesItemCode)}
      />
      
    <BatchSelectionPopup
  open={showBatchPopup}
  onClose={() => setShowBatchPopup(false)}
  batchData={batchData}
  onSelectBatch={handleBatchSelect}
  productSettings={productSettings}
  companySettings={companySettings}
  requiredQuantity={selectedBatchIndex !== null ? parseFloat(items[selectedBatchIndex]?.quantity) || 0 : 0}
  selectedBatches={selectedBatchIndex !== null ? items[selectedBatchIndex]?.batches || [] : []}
  productCode={selectedBatchIndex !== null ? items[selectedBatchIndex]?.productCode : ''}
  warehouseCode={selectedBatchIndex !== null ? items[selectedBatchIndex]?.warehouseCode : ''}
  warehouseFiltering={batchData.warehouseFiltering}
/>
      

<SerialSelectionPopup
  open={showSerialPopup}
  onClose={() => setShowSerialPopup(false)}
  serialData={serialData}
  onSelectSerial={handleSerialSelect}
  requiredQuantity={selectedSerialIndex !== null ? parseFloat(items[selectedSerialIndex]?.quantity) || 0 : 0}
  selectedSerials={selectedSerialIndex !== null ? items[selectedSerialIndex]?.serials || [] : []}
  productCode={selectedSerialIndex !== null ? items[selectedSerialIndex]?.productCode : ''} // Ensure this is passed
  warehouseCode={selectedSerialIndex !== null ? items[selectedSerialIndex]?.warehouseCode : ''}
  warehouseFiltering={serialData.warehouseFiltering}
/>
      
      <div className={`fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 ${isMaximized ? 'p-0' : 'p-4'}`} style={{zIndex:1000}}>
        <div className={`bg-white shadow-2xl w-full flex flex-col relative z-10 overflow-hidden transition-all duration-300 ${
          isMaximized ? 'h-[100vh] max-w-full' : 'h-[100vh] max-w-6xl'
        }`}>
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Delivery Note' : 'Create Delivery Note'}</h2>
              <div className="flex space-x-2">
                <button 
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                  onClick={toggleMaximize}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>
          
          {/* User validation warning */}
          {open && !isUserValid && (
            <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {authError || 'User information not available. Please refresh the page and log in again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Scrollable Content Area */}
          <div className="flex-grow overflow-y-auto px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              {/* Delivery Note Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Delivery Note Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Delivery Note Number *</label>
                    <input 
                      name="deliveryNoteNo" 
                      value={header.deliveryNoteNo} 
                      onChange={handleHeaderChange} 
                      disabled
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input"
                    />
                    {errors.deliveryNoteNo && <span className="text-xs text-red-500">{errors.deliveryNoteNo}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Delivery Note Date *</label>
                    <input 
                      type="date" 
                      name="deliveryNoteDate" 
                      value={header.deliveryNoteDate} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.deliveryNoteDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                    />
                    {errors.deliveryNoteDate && <span className="text-xs text-red-500">{errors.deliveryNoteDate}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Due Date *</label>
                    <input 
                      type="date" 
                      name="dueDate" 
                      value={header.dueDate} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.dueDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                    />
                    {errors.dueDate && <span className="text-xs text-red-500">{errors.dueDate}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Customer Name *</label>
                    <SearchableDropdown
                      options={dropdownData.customers || []}
                      value={header.customerCode}
                      onChange={(customerCode) => {
                        handleHeaderChange({
                          target: { name: 'customerCode', value: customerCode }
                        });
                      }}
                      getOptionLabel={(customer) => customer.customer_name}
                      getOptionValue={(customer) => customer.customer_code}
                      getOptionSecondaryLabel={(customer) => customer.customer_code}
                      placeholder="SELECT CUSTOMER"
                      error={errors.customerCode}
                      className="uppercase"
                    />
                    {errors.customerCode && <span className="text-xs text-red-500">{errors.customerCode}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                    <SearchableDropdown
                      options={dropdownData.branches || []}
                      value={header.branchCode}
                      onChange={(branchCode) => {
                        handleHeaderChange({
                          target: { name: 'branchCode', value: branchCode }
                        });
                      }}
                      getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
                      getOptionValue={(branch) => branch.BranchCode}
                      getOptionSecondaryLabel={(branch) => branch.BranchName}
                      placeholder="SELECT BRANCH"
                      className="uppercase"
                    />
                  </div>
                  
                  {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Department *
                      </label>
                      <select
                        name="department_id"
                        value={header.department_id}
                        onChange={handleHeaderChange}
                        className={`w-full border rounded-lg px-3 py-2 ${
                          errors.department_id ? "border-red-400" : "border-[#E2E8F0]"
                        } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                        disabled={loadingDepartments}
                      >
                        <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                        {departments.map((dept) => (
                          <option key={dept.DepartmentID} value={dept.DepartmentID}>
                            {dept.DepartmentCode} - {dept.DepartmentName}
                          </option>
                        ))}
                      </select>
                      {errors.department_id && (
                        <span className="text-xs text-red-500">
                          {errors.department_id}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <select 
                      name="isActive" 
                      value={header.isActive} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      <option value="1">ACTIVE</option>
                      <option value="0">INACTIVE</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Update Inventory</label>
                    <select 
                      name="inventoryFlag" 
                      value={header.inventoryFlag} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      <option value="yes">YES</option>
                      <option value="no">NO</option>
                    </select>
                    <span className="text-xs text-gray-500">Select "Yes" to update inventory with this delivery note</span>
                  </div>
                </div>
                
                {/* Customer Information */}
                {customerInfo && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-md font-semibold text-blue-800 mb-2">Customer Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-gray-600">Customer Code:</span>
                        <span className="font-medium ml-2">{customerInfo.customer_code}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Customer Name:</span>
                        <span className="font-medium ml-2">{customerInfo.customer_name}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sales Order Selection */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Select Sales Order Items</h3>
                  <button
                    type="button"
                    className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#5a7997] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    onClick={handleOpenSalesOrderModal}
                    disabled={!header.customerCode}
                  >
                    <PlusIcon size={16} className="mr-1" />
                    Select Sales Order Items
                  </button>
                </div>
                
                {header.customerCode ? (
                  <>
                    {selectedSalesOrderItems.length > 0 ? (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {[...new Set(selectedSalesOrderItems.map(item => item.SalesOrderNumber))].map(orderNumber => (
                            <span key={orderNumber} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {orderNumber}
                              <button
                                type="button"
                                className="ml-2 text-blue-600 hover:text-blue-900"
                                onClick={() => {
                                  // Remove all items of this sales order
                                  const newSelectedItems = selectedSalesOrderItems.filter(item => item.SalesOrderNumber !== orderNumber);
                                  setSelectedSalesOrderItems(newSelectedItems);
                                  
                                  // Also update the items in the delivery note
                                  const newDeliveryNoteItems = items.filter(item => item.salesOrderNumber !== orderNumber);
                                  setItems(newDeliveryNoteItems);
                                }}
                              >
                                <CloseIcon size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4 text-center py-4 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                        No sales order items selected. Click "Select Sales Order Items" to add items to this delivery note.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Please select a customer to view their sales order items
                  </div>
                )}
                
                {/* Delivery Note Items */}
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-[#384959] mb-2">Delivery Note Items</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2">Product Code</th>
                          <th className="p-2">Product</th>
                          <th className="p-2">Sales Order</th>
                          <th className="p-2">Description</th>
                          <th className="p-2">UOM</th>
                          <th className="p-2">Quantity</th>
                          <th className="p-2">Unit Price</th>
                          <th className="p-2">Discount</th>
                          <th className="p-2">Tax</th>
                          <th className="p-2">Tax Rate</th>
                          <th className="p-2">Tax Amount</th>
                          <th className="p-2">Total</th>
                          <th className="p-2">Warehouse</th>
                          <th className="p-2">Tracking Type</th>
                          <th className="p-2">Tracking Details</th>
                          
                          {/* Department column - Only shown if ShowOnItem is true */}
                          {showDepartmentItemField && <th className="p-2">Department</th>}
                          <th className="p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length > 0 ? (
                          items.map((item, idx) => (
                            <tr key={idx} className="border-t border-[#E2E8F0]">
                              <td className="p-2">
                                <input 
                                  name="productCode" 
                                  value={item.productCode} 
                                  readOnly
                                  className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase-input"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="productName" 
                                  value={item.productName} 
                                  readOnly
                                  className="w-32 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase-input"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="salesOrderNumber" 
                                  value={item.salesOrderNumber} 
                                  readOnly
                                  className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase-input"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="description" 
                                  value={item.description} 
                                  readOnly
                                  className="w-40 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase-input"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="uomID" 
                                  value={item.uomID} 
                                  readOnly
                                  className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="quantity" 
                                  type="number" 
                                  min="0" 
                                  max={item.remainingQty}
                                  value={item.quantity} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  className={`w-16 border rounded-lg px-2 py-1 ${
                                    (stockValidation[idx]?.valid === false) 
                                      ? 'quantity-exceeded border-red-400 bg-red-50' 
                                      : 'border-[#E2E8F0]'
                                  } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                  placeholder="0"
                                />
                                {stockValidation[idx]?.valid === false && (
                                  <span className="text-xs text-red-500 mt-1">
                                    {stockValidation[idx].message}
                                  </span>
                                )}
                                {errors[`quantity_${idx}`] && <span className="text-xs text-red-500">{errors[`quantity_${idx}`]}</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="unitPrice" 
                                  type="number" 
                                  min="0" 
                                  step="0.01"
                                  value={item.unitPrice} 
                                  readOnly
                                  onChange={e => handleItemChange(idx, e)} 
                                  className="w-20 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="discount" 
                                  type="number" 
                                  min="0" 
                                  step="0.01"
                                  value={item.discount} 
                                  readOnly
                                  onChange={e => handleItemChange(idx, e)} 
                                  className="w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="taxCode" 
                                  value={item.taxCode} 
                                  readOnly
                                  className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              <td className="p-2">
                                <div className="flex items-center">
                                  <span className="text-sm"> {item.taxCode ? `${getTaxRate(item.taxCode)}%` : 'N/A'}</span>
                                </div>
                              </td>
                              <td className="p-2">
                                <input 
                                  name="taxAmount" 
                                  value={item.taxAmount} 
                                  readOnly
                                  className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="total" 
                                  value={item.total} 
                                  readOnly
                                  className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              <td className="p-2">
                                <select 
                                  name="warehouseCode" 
                                  value={item.warehouseCode || ''} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  className={`w-32 border rounded-lg px-2 py-1 ${errors[`warehouseCode_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                >
                                  <option value="">Select Warehouse</option>
                                  {dropdownData.locations.map(location => (
                                    <option key={location.LocationCode} value={location.LocationCode}>
                                      {location.LocationName}
                                    </option>
                                  ))}
                                </select>
                                {errors[`warehouseCode_${idx}`] && <span className="text-xs text-red-500">{errors[`warehouseCode_${idx}`]}</span>}
                              </td>
                             <td className="p-2">
  <span className="text-sm font-medium">
    {item.trackingType === 'batch' ? 'Batch' : 
     item.trackingType === 'serial' ? 'Serial' : 'None'}
  </span>
</td>
                            <td className="p-2">
  {item.trackingType === 'batch' ? (
    <div>
      <button
        type="button"
        className="text-blue-600 hover:text-blue-800 text-sm"
        onClick={() => handleBatchClick(idx, item.productCode, item.warehouseCode)}
      >
        {item.batches && item.batches.length > 0 
          ? `${item.batches.length} batch(es)` 
          : 'Select Batches'}
      </button>
      {errors[`batches_${idx}`] && (
        <span className="text-xs text-red-500 block">{errors[`batches_${idx}`]}</span>
      )}
    </div>
      ) : item.trackingType === 'serial' ? (
    <div>
      <button
        type="button"
        className="text-blue-600 hover:text-blue-800 text-sm"
        onClick={() => handleSerialClick(idx, item.productCode, item.warehouseCode)}
      >
        {item.serials && item.serials.length > 0 
          ? `${item.serials.length} serial(s)` 
          : 'Select Serials'}
      </button>
      {errors[`serials_${idx}`] && (
        <span className="text-xs text-red-500 block">{errors[`serials_${idx}`]}</span>
      )}
    </div>
  ) : (
    <span className="text-gray-500">N/A</span>
  )}
</td>
                              {/* Department cell - Only shown if ShowOnItem is true */}
                              {showDepartmentItemField && (
                                <td className="p-2">
                                  <select
                                    name="department_id"
                                    value={item.department_id}
                                    onChange={(e) => handleItemChange(idx, e)}
                                    className={`w-24 border rounded-lg px-2 py-1 ${
                                      errors[`department_id_${idx}`]
                                        ? "border-red-400"
                                        : "border-[#E2E8F0]"
                                    } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                  >
                                    <option value="">Select</option>
                                    {departments.map((dept) => (
                                      <option key={dept.DepartmentID} value={dept.DepartmentID}>
                                        {dept.DepartmentCode}
                                      </option>
                                    ))}
                                  </select>
                                  {errors[`department_id_${idx}`] && (
                                    <span className="text-xs text-red-500">*</span>
                                  )}
                                </td>
                              )}

                               <td className="p-2">
            <button
              type="button"
              className="text-red-600 hover:text-red-800"
              onClick={() => handleDeleteItem(idx)}
              title="Delete item"
            >
              <CloseIcon size={16} />
            </button>
          </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={showDepartmentItemField ? 16 : 15} className="text-center py-4 text-gray-500">
                              No items found. Please select sales order items to populate the delivery note.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Summary Section */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Subtotal</span>
                      <span className="text-base font-semibold">{header.subTotal}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Tax</span>
                      <span className="text-base font-semibold">{header.totalTax}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Discount</span>
                      <span className="text-base font-semibold">{header.discount}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Shipping & Handling</span>
                      <input
                        type="number"
                        name="shippingHandlingCharge"
                        value={header.shippingHandlingCharge}
                        onChange={handleHeaderChange}
                        min="0"
                        step="0.01"
                        className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Amount</span>
                      <span className="text-base font-semibold text-lg text-blue-600">{header.totalAmount}</span>
                    </div>
                  </div>
                </div>
                
                {/* Payment Section */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Payment Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-[#64748b]">Amount Paid</label>
                      <input
                        type="number"
                        name="amountPaid"
                        value={header.amountPaid}
                        onChange={handleHeaderChange}
                        min="0"
                        step="0.01"
                        max={parseFloat(header.totalAmount) || 0}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-[#64748b]">Balance Due</label>
                      <input
                        type="number"
                        name="balanceDue"
                        value={header.balanceDue}
                        onChange={handleHeaderChange}
                        min="0"
                        step="0.01"
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-[#64748b]">Payment Method</label>
                      <select
                        name="paymentMethod"
                        value={header.paymentMethod}
                        onChange={handleHeaderChange}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      >
                        <option value="">Select Payment Method</option>
                        <option value="Cash">Cash</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Check">Check</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="flex flex-col md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">Payment Notes</label>
                      <textarea
                        name="paymentNotes"
                        value={header.paymentNotes}
                        onChange={handleHeaderChange}
                        rows={2}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          {/* Fixed Footer with Buttons */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                disabled={loading || isSubmitting || !isUserValid}
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Delivery Note' : 'Create Delivery Note')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceFormModal;